import { PlusSquareFilledIcon } from "Icons/PlusSquareFilled";

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SerializedStyles, css } from "@emotion/react";

import { trackEvent } from "interfaces/tracker";

import { Button, Props as ButtonProps } from "components/shared/library/Button";

import { useAlert } from "contexts/alertContext";

import { blue, uiBlueGray, uiGray } from "constants/colors";
import { maxMediaSizeInMb } from "constants/files";

import { ButtonWrapper } from "./ButtonWrapper";
import { FileInput } from "./FileInput";
import { Typography } from "./Typography";

const style = {
  mediaUploader: css({ display: "flex", flexDirection: "column", gap: 8 }),
  iconLeft: css({ paddingTop: 4 }),
  dropUpload: css({
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 156,
    gap: 8,
    background: blue[100],
    border: `2px solid ${blue[300]}`,
    borderRadius: 8,
  }),
  draggedOver: css({
    background: blue[200],
    border: `2px solid ${blue[400]}`,
    ".plus-square-filled-icon path": {
      stroke: blue[700],
    },
  }),

  iconDiv: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    background: uiBlueGray[50],
    borderRadius: 8,
  }),
  dropUploadText: css({
    display: "flex",
    flexDirection: "column",
    gap: 4,
  }),
};

export type Props = {
  onChange: (files: File[]) => void;
  buttonText?: string;
  isDisabled?: boolean;
  isButton?: boolean;
  acceptedFileTypes: string;
  fileTypeString?: string;
  maxSizeInMb?: number;
  buttonProps?: ButtonProps;
  customLabel?: string;
  customCss?: { dropUpload?: SerializedStyles };
  showFileTypesLabel?: boolean;
  multiple?: boolean;
};

export const MediaUploader = ({
  onChange,
  buttonText = "Upload media",
  isDisabled = false,
  isButton,
  acceptedFileTypes,
  fileTypeString,
  maxSizeInMb = maxMediaSizeInMb,
  buttonProps,
  customLabel = "Drag, drop or select a file",
  customCss,
  showFileTypesLabel = true,
  multiple,
}: Props) => {
  const imageUploader = useRef<HTMLInputElement>(null);
  const { createAlert } = useAlert();
  const [isFileDraggedOver, setIsFileDraggedOver] = useState(false);
  const { t } = useTranslation();
  return (
    <>
      {isButton ? (
        <Button
          onClick={() => imageUploader.current?.click()}
          {...buttonProps}
        />
      ) : (
        <div css={style.mediaUploader}>
          <ButtonWrapper
            ariaLabel={buttonText}
            onClick={() => imageUploader.current?.click()}
            onDrop={async (e) => {
              setIsFileDraggedOver(false);
              e.preventDefault();

              const files = Array.from(e.dataTransfer?.files);

              if (!files.length) return;

              if (
                files.some((file) => !acceptedFileTypes.includes(file.type))
              ) {
                return createAlert(
                  "error",
                  `File${files.length > 1 ? "s" : ""} must be ${
                    fileTypeString || acceptedFileTypes
                  }`
                );
              }
              const fileTooBig = files.find(
                (file) => file.size > maxSizeInMb * 1024 * 1024
              );
              if (fileTooBig) {
                // track the event in mixpanel
                trackEvent("FileTooLarge", {
                  fileSize: fileTooBig.size,
                  maxSizeInMb,
                  fileType: fileTooBig.type,
                  error: `File size is too large. Please try again with a file under ${maxSizeInMb}MB.`,
                });
                return createAlert(
                  "error",
                  t(
                    "File size is too large. Please try again with a file under {{maxSizeInMb}}MB.",
                    { maxSizeInMb }
                  )
                );
              }

              onChange(multiple ? files : files.slice(0, 1));
            }}
            onDragEnter={() => {
              setIsFileDraggedOver(true);
            }}
            onDragLeave={() => {
              setIsFileDraggedOver(false);
            }}
            onDragOver={(e) => {
              // we need to prevent default on hover in order to prevent default on drop
              e.preventDefault();
            }}
            disabled={isDisabled}
            customCss={[
              style.dropUpload,
              customCss?.dropUpload,
              isFileDraggedOver ? style.draggedOver : undefined,
            ]}
          >
            <PlusSquareFilledIcon stroke={blue[800]} width={14} height={14} />
            <Typography
              color={isFileDraggedOver ? blue[700] : blue[800]}
              size="XS"
            >
              {customLabel}
            </Typography>
          </ButtonWrapper>
          {showFileTypesLabel && (
            <Typography color={uiGray[80]} size="XXS">
              {t("Supported file types: {{fileTypesString}}", {
                fileTypesString: fileTypeString,
              })}
            </Typography>
          )}
        </div>
      )}
      <FileInput
        onChange={onChange}
        imageFileInputRef={imageUploader}
        isDisabled={isDisabled}
        acceptedFileTypes={acceptedFileTypes}
        maxSizeInMb={maxSizeInMb}
        multiple={multiple}
        fileTypeString={fileTypeString}
      />
    </>
  );
};
